import React, { useState, useEffect } from "react";
import {
  Github,
  Linkedin,
  Mail,
  ExternalLink,
  PieChart,
  Menu,
  X,
  ArrowRight,
  Cpu,
  Database,
  Sparkles,
  Zap,
  Code2,
  LineChart,
  BrainCircuit,
  Terminal,
  Globe,
  GraduationCap,
  BarChart3,
  TrendingUp,
  Calendar,
  BookOpen,
} from "lucide-react";

// --- PORTFOLIO DATA ---
const DATA = {
  personal: {
    name: "Xiaolei (Krystal) Qin",
    role1: "Strategy Analyst",
    role2: "Data Analyst",
    tagline1: "Translating business drivers into strategic roadmaps.",
    tagline2: "Turning raw data into high-fidelity actionable insights.",
    location: "New York, NY",
    email: "Qinkrystal@gmail.com",
    linkedin: "https://www.linkedin.com/in/krystalqin",
    bookingLink: "https://calendar.app.google/nioGVu7TUB8e1jJE8",
    github: "https://github.com",
    // Updated to the jpg version provided
    photoUrl: "/IMG_7966.PNG",
    bio: "I specialize in the intersection of business strategy and technical analytics. Currently pursuing an MS in Applied Analytics at Columbia University, I leverage my Finance background to build data-driven frameworks that solve complex business challenges—from financial modeling to AI-enhanced automation.",
  },
  education: [
    {
      school: "Columbia University",
      degree: "Master of Science in Applied Analytics",
      period: "2025 - 2027",
      location: "New York, NY",
      courses:
        "Applied Machine Learning, AI User Interfaces for Analytics, Fundamentals of Data Engineering, Storytelling with Data and AI",
    },
    {
      school: "University of Toronto",
      degree: "Bachelor of Commerce, Finance Specialist",
      period: "2021 - 2025",
      location: "Toronto, ON",
      courses:
        "Coding and Data Management for Business Analytics, Fintech, Blockchain & Decentralized Finance",
    },
  ],
  skills: {
    strategy: [
      { name: "Strategic Modeling", icon: <TrendingUp size={14} /> },
      { name: "Market Analysis", icon: <PieChart size={14} /> },
      { name: "Business Intelligence", icon: <BarChart3 size={14} /> },
      { name: "Cross-functional Leadership", icon: <Globe size={14} /> },
    ],
    technical: [
      { name: "Python / SQL / Stata", icon: <Terminal size={14} /> },
      { name: "Data Engineering", icon: <Database size={14} /> },
      { name: "Machine Learning", icon: <Cpu size={14} /> },
      { name: "Tableau (Certified)", icon: <LineChart size={14} /> },
    ],
  },
  experience: [
    {
      company: "RealValue Capital",
      role: "Financial Analyst",
      period: "Dec 2025 - Present",
      description:
        "Analyzing B2B AI Agent sectors and SaaS verticals to identify investment targets. Developing data-backed Individual IP scripts and cost-reduction models for cross-border commerce.",
      tags: ["Investment Research", "SaaS"],
    },
    {
      company: "Zhejiang Chouzhou Bank",
      role: "Financial Associate Intern",
      period: "May 2024 - Jul 2024",
      description:
        "Maintained data integrity for high-volume transactions. Optimized audit workflows ensuring 100% compliance with internal standards.",
      tags: ["Data Integrity", "Audit"],
    },
    {
      company: "UTM Moves",
      role: "Events Lead",
      period: "Sep 2023 - Apr 2024",
      description:
        "Pioneered a data-centric planning model using Excel-based forecasting to analyze student engagement trends, resulting in a 15% increase in participation.",
      tags: ["Forecasting", "Engagement"],
    },
    {
      company: "EPIE International Platform",
      role: "Strategy Analyst",
      period: "Aug 2021 - Apr 2024",
      description:
        "Engineered a campus-wide engagement framework by analyzing survey data from 200+ students. Translated behavioral insights into marketing strategies, increasing participation by 10%.",
      tags: ["Behavioral Modeling", "Marketing Strategy"],
    },
  ],
  projects: [
    {
      title: "AI Prompt Engineering Hub",
      link: "https://refineprompt.kry.it.com/",
      description:
        "Architected a full-stack platform on Vercel utilizing Google Gemini APIs to transform unstructured inputs into high-fidelity prompts. Monitored token utilization and response latency via Power BI.",
      tags: ["React", "Gemini API", "Vercel", "Power BI"],
      color: "cyan",
      icon: <BrainCircuit size={24} />,
      hasLine: true,
    },
    {
      title: "MeadowBridge Systems",
      link: "#",
      description:
        "Led a cross-functional project optimizing financial reporting. Built SQL queries to analyze latency and used Python (pandas) to automate reconciliation, reducing manual processing by 35%.",
      tags: ["SQL", "Python", "Agile", "Optimization"],
      color: "orange",
      icon: <TrendingUp size={24} />,
      hasLine: false,
    },
    {
      title: "BrightPath Data Hub",
      link: "#",
      description:
        "Engineered an end-to-end reporting prototype. Standardized data aggregation workflows for revenue reconciliation, reducing manual aggregation time by 30%.",
      tags: ["ETL", "Tableau", "Power BI", "SQL"],
      color: "cyan",
      icon: <BarChart3 size={24} />,
      hasLine: false,
    },
  ],
};

export default function App() {
  const [hoverSide, setHoverSide] = useState(null);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
      const reveals = document.querySelectorAll(".reveal");
      reveals.forEach((reveal) => {
        const rect = reveal.getBoundingClientRect();
        if (rect.top < window.innerHeight - 100)
          reveal.classList.add("active-reveal");
      });
    };
    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-[#faf9f6] font-sans selection:bg-orange-200 selection:text-orange-900 scroll-smooth overflow-x-hidden">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,700;1,400&family=Inter:wght@300;400;600;700&family=JetBrains+Mono:wght@400;700&display=swap');
        .font-serif { font-family: 'Playfair Display', serif; }
        .font-mono { font-family: 'JetBrains Mono', monospace; }
        .reveal { opacity: 0; transform: translateY(20px); transition: all 0.7s cubic-bezier(0.2, 0, 0.2, 1); }
        .active-reveal { opacity: 1; transform: translateY(0); }
        .hero-split-transition { transition: all 0.9s cubic-bezier(0.2, 0.8, 0.2, 1); }
        
        .hero-text { 
            font-size: clamp(2.2rem, 6vw, 6.5rem);
            line-height: 0.9;
        }

        @media (max-width: 768px) {
          .hero-text { font-size: 2.8rem; }
        }
      `}</style>

      {/* --- NAVIGATION --- */}
      <nav
        className={`fixed top-0 w-full z-[100] transition-all duration-500 ${
          scrolled
            ? "bg-white/80 backdrop-blur-lg border-b border-gray-100 py-3"
            : "bg-transparent py-6"
        }`}
      >
        <div className="container mx-auto px-6 flex justify-between items-center">
          <div
            className={`text-2xl font-bold tracking-tighter transition-colors duration-500 ${
              hoverSide === "right" && !scrolled
                ? "text-white"
                : "text-gray-900"
            }`}
          >
            KQ<span className="text-orange-500">.</span>
          </div>
          <div className="hidden md:flex items-center gap-10">
            {["About", "Education", "Experience", "Projects"].map((item) => (
              <a
                key={item}
                href={`#${item.toLowerCase()}`}
                className={`text-[10px] font-bold uppercase tracking-[0.2em] transition-all hover:text-orange-500 ${
                  hoverSide === "right" && !scrolled
                    ? "text-gray-400"
                    : "text-gray-600"
                }`}
              >
                {item}
              </a>
            ))}
            <a
              href="https://calendar.app.google/nioGVu7TUB8e1jJE8"
              target="_blank"
              rel="noreferrer"
              // I have restored the full styling classes below:
              className="px-6 py-2 bg-gray-900 text-white text-[10px] font-bold uppercase tracking-widest rounded-full hover:bg-orange-600 transition-all shadow-lg hover:shadow-orange-500/20 cursor-pointer"
            >
              Book Call
            </a>
          </div>
        </div>
      </nav>

      {/* --- HERO SECTION --- */}
      <section className="relative h-screen flex flex-col md:flex-row overflow-hidden bg-white">
        {/* LEFT: STRATEGY ANALYST */}
        <div
          className={`relative h-1/2 md:h-full flex flex-col justify-center items-center md:items-end p-8 md:pr-[22vw] lg:pr-[25vw] hero-split-transition cursor-crosshair z-10 ${
            hoverSide === "right"
              ? "md:w-[20%] bg-[#faf9f6]"
              : hoverSide === "left"
              ? "md:w-[80%] bg-white"
              : "md:w-1/2 bg-[#faf9f6]"
          }`}
          onMouseEnter={() => setHoverSide("left")}
          onMouseLeave={() => setHoverSide(null)}
        >
          <div
            className={`transition-all duration-700 max-w-lg text-right ${
              hoverSide === "right"
                ? "opacity-0 scale-90 blur-sm pointer-events-none"
                : "opacity-100"
            }`}
          >
            <div className="flex items-center justify-end gap-3 mb-6">
              <span className="h-[1px] w-12 bg-orange-200"></span>
              <span className="text-orange-600 font-serif italic text-lg flex items-center gap-2">
                <Sparkles size={18} /> The Strategist
              </span>
            </div>
            <h1 className="hero-text font-serif font-medium tracking-tighter text-gray-900 mb-6">
              Strategy<span className="text-orange-500">.</span>
            </h1>
            <p className="font-serif italic text-gray-400 text-base md:text-lg leading-relaxed mb-8">
              {DATA.personal.tagline1}
            </p>
            <div
              className={`flex flex-wrap justify-end gap-3 transition-all duration-700 ${
                hoverSide === "left"
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 translate-y-4"
              }`}
            >
              {DATA.skills.strategy.map((s) => (
                <div
                  key={s.name}
                  className="flex items-center gap-2 px-4 py-2 bg-orange-50 text-orange-700 border border-orange-100 rounded-full text-[9px] font-bold uppercase tracking-widest"
                >
                  {s.icon} {s.name}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* RIGHT: DATA ANALYST */}
        <div
          className={`relative h-1/2 md:h-full flex flex-col justify-center items-center md:items-start p-8 md:pl-[22vw] lg:pl-[25vw] hero-split-transition cursor-terminal z-10 ${
            hoverSide === "left"
              ? "md:w-[20%] bg-[#1a1a1a]"
              : hoverSide === "right"
              ? "md:w-[80%] bg-[#0f0f0f]"
              : "md:w-1/2 bg-[#1a1a1a]"
          }`}
          onMouseEnter={() => setHoverSide("right")}
          onMouseLeave={() => setHoverSide(null)}
        >
          <div
            className={`transition-all duration-700 max-w-lg text-left ${
              hoverSide === "left"
                ? "opacity-0 scale-90 blur-sm pointer-events-none"
                : "opacity-100"
            }`}
          >
            <div className="flex items-center gap-3 mb-6">
              <span className="text-cyan-400 font-mono text-[10px] font-bold tracking-[0.3em] uppercase flex items-center gap-2">
                <Database size={16} /> [ The Analyst ]
              </span>
              <span className="h-[1px] w-12 bg-cyan-900/50"></span>
            </div>
            <h1 className="hero-text font-sans font-bold tracking-tighter text-white mb-6">
              Data<span className="text-cyan-500">_</span>
            </h1>
            <p className="font-mono text-gray-500 text-xs md:text-sm leading-relaxed mb-8">
              {DATA.personal.tagline2}
            </p>
            <div
              className={`flex flex-wrap gap-3 transition-all duration-700 ${
                hoverSide === "right"
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 translate-y-4"
              }`}
            >
              {DATA.skills.technical.map((s) => (
                <div
                  key={s.name}
                  className="flex items-center gap-2 px-4 py-2 bg-cyan-950/30 text-cyan-400 border border-cyan-500/20 rounded-md text-[9px] font-mono font-bold uppercase tracking-widest"
                >
                  {s.icon} {s.name}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* FLOATING PHOTO: Center Circle - Large shift to avoid covering text */}
        <div
          className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 pointer-events-none hero-split-transition hidden md:block ${
            hoverSide === "left"
              ? "translate-x-[35vw] scale-105"
              : hoverSide === "right"
              ? "-translate-x-[35vw] scale-105"
              : ""
          }`}
        >
          <div className="relative group transition-all duration-700">
            <div
              className={`absolute -inset-6 rounded-full border border-dashed transition-all duration-700 animate-[spin_15s_linear_infinite] ${
                hoverSide === "right"
                  ? "border-cyan-500/40 scale-110"
                  : hoverSide === "left"
                  ? "border-orange-500/40 scale-110"
                  : "border-gray-200 opacity-20"
              }`}
            ></div>

            <div className="w-64 lg:w-80 h-64 lg:h-80 rounded-full overflow-hidden border-[6px] border-white shadow-[0_32px_64px_-16px_rgba(0,0,0,0.3)] bg-gray-100">
              <img
                src={DATA.personal.photoUrl}
                alt={DATA.personal.name}
                className="w-full h-full object-cover scale-105"
                onError={(e) => {
                  e.target.src =
                    "https://ui-avatars.com/api/?name=Krystal+Qin&background=000&color=fff";
                }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* --- ABOUT --- */}
      <section id="about" className="py-32 bg-white">
        <div className="container mx-auto px-6 max-w-5xl">
          <div className="grid md:grid-cols-12 gap-16 items-start reveal">
            <div className="md:col-span-7">
              <span className="text-orange-500 font-bold tracking-[0.3em] uppercase text-[10px] mb-8 block">
                Positioning
              </span>
              <h2 className="text-4xl md:text-6xl font-serif leading-[1.1] text-gray-900 mb-10">
                Bridging{" "}
                <span className="italic text-orange-600">Complex Data</span>{" "}
                with <span className="italic">Strategic Clarity.</span>
              </h2>
              <p className="text-gray-600 text-xl leading-relaxed">
                {DATA.personal.bio}
              </p>
            </div>
            <div className="md:col-span-5 space-y-4 pt-4">
              <div className="p-8 bg-gray-50 rounded-3xl border border-gray-100 flex items-start gap-4">
                <Globe size={24} className="text-orange-500 flex-shrink-0" />
                <div>
                  <h4 className="font-bold text-sm uppercase tracking-widest mb-2">
                    Global Communication
                  </h4>
                  <p className="text-xs text-gray-500 font-bold">
                    English • Mandarin • Cantonese
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* --- EDUCATION --- */}
      <section id="education" className="py-32 bg-[#faf9f6]">
        <div className="container mx-auto px-6 max-w-4xl">
          <div className="flex items-center gap-6 mb-20 reveal">
            <h2 className="text-4xl font-serif font-bold">Education</h2>
            <div className="h-px bg-gray-200 flex-1"></div>
          </div>
          <div className="space-y-12">
            {DATA.education.map((edu, idx) => (
              <div key={idx} className="reveal group flex gap-8 items-start">
                <div className="p-4 bg-white rounded-2xl shadow-sm group-hover:bg-orange-500 group-hover:text-white transition-all">
                  <GraduationCap size={32} />
                </div>
                <div>
                  <div className="text-orange-500 font-mono text-xs font-bold tracking-widest mb-1">
                    {edu.period}
                  </div>
                  <h3 className="text-2xl font-bold mb-1">{edu.school}</h3>
                  <div className="text-lg font-serif italic text-gray-600 mb-3">
                    {edu.degree}
                  </div>
                  <div className="flex items-start gap-2 mt-4 p-4 bg-white/50 rounded-xl border border-gray-100">
                    <BookOpen
                      size={16}
                      className="text-gray-400 mt-1 flex-shrink-0"
                    />
                    <p className="text-xs text-gray-500 font-mono leading-relaxed">
                      <span className="font-bold text-gray-700 uppercase tracking-tighter mr-2">
                        Core Courses:
                      </span>
                      {edu.courses}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* --- EXPERIENCE --- */}
      <section id="experience" className="py-32 bg-white">
        <div className="container mx-auto px-6 max-w-4xl">
          <div className="flex items-center gap-6 mb-20 reveal">
            <h2 className="text-4xl font-serif font-bold">
              Internship & Experience
            </h2>
            <div className="h-px bg-gray-200 flex-1"></div>
          </div>
          <div className="space-y-6">
            {DATA.experience.map((job, idx) => (
              <div
                key={idx}
                className="reveal group bg-[#faf9f6] border border-gray-100 rounded-3xl p-8 hover:bg-white hover:shadow-2xl hover:shadow-black/5 transition-all duration-500"
              >
                <div className="flex flex-col md:flex-row gap-6">
                  <div className="md:w-1/3">
                    <div className="text-orange-500 font-mono text-xs font-bold tracking-widest mb-2">
                      {job.period}
                    </div>
                    <h3 className="text-xl font-black text-gray-900 leading-tight">
                      {job.company}
                    </h3>
                    <div className="flex flex-wrap gap-2 mt-4">
                      {job.tags.map((t) => (
                        <span
                          key={t}
                          className="px-2 py-1 bg-white text-gray-400 text-[9px] font-bold uppercase tracking-widest rounded-md"
                        >
                          {t}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="md:w-2/3">
                    <h4 className="text-lg font-serif italic text-gray-800 mb-3 flex items-center gap-3">
                      {job.role}{" "}
                      <ArrowRight
                        size={16}
                        className="opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all"
                      />
                    </h4>
                    <p className="text-gray-500 leading-relaxed text-sm">
                      {job.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* --- PROJECTS --- */}
      <section id="projects" className="py-32 bg-gray-900 text-white">
        <div className="container mx-auto px-6 max-w-6xl">
          <div className="text-center mb-24 reveal">
            <span className="text-cyan-500 font-mono text-[10px] font-bold tracking-[0.4em] uppercase mb-4 block">
              Execution & Methodology
            </span>
            <h2 className="text-5xl font-serif font-bold">Selected Projects</h2>
            <p className="text-gray-500 mt-6 max-w-xl mx-auto text-sm font-serif italic">
              A selection of projects where I bridge technical analysis with
              business strategy.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {DATA.projects.map((p, i) => (
              <a
                key={i}
                href={p.link}
                target={p.link === "#" ? "_self" : "_blank"}
                rel="noreferrer"
                className="reveal group bg-[#111] border border-white/5 rounded-[2.5rem] p-10 h-full flex flex-col justify-between hover:border-white/20 hover:shadow-3xl hover:shadow-cyan-500/10 transition-all duration-500"
              >
                <div>
                  <div className="flex justify-between items-start mb-10">
                    <div
                      className={`p-4 rounded-2xl ${
                        p.color === "cyan"
                          ? "bg-cyan-500/10 text-cyan-400"
                          : "bg-orange-500/10 text-orange-400"
                      }`}
                    >
                      {p.icon}
                    </div>
                  </div>
                  <h3 className="text-2xl font-bold mb-4">{p.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed mb-8">
                    {p.description}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {p.tags.map((t) => (
                      <span
                        key={t}
                        className="text-[9px] font-mono text-gray-600 border border-white/5 px-2 py-1 rounded-full"
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                </div>
                <div
                  className={`mt-12 pt-8 flex items-center justify-between group-hover:translate-y-[-4px] transition-transform min-h-[40px] ${
                    p.hasLine ? "border-t border-white/5" : ""
                  }`}
                >
                  {p.link !== "#" && (
                    <>
                      <span className="text-[9px] font-bold tracking-[0.3em] text-gray-700 uppercase">
                        Live Platform
                      </span>
                      <div
                        className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                          p.color === "cyan"
                            ? "bg-cyan-500/10 text-cyan-400"
                            : "bg-orange-500/10 text-orange-400"
                        }`}
                      >
                        <ExternalLink size={14} />
                      </div>
                    </>
                  )}
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* --- FOOTER --- */}
      <footer id="contact" className="bg-white py-24 border-t border-gray-100">
        <div className="container mx-auto px-6 max-w-4xl text-center reveal">
          <h2 className="text-4xl md:text-6xl font-serif font-bold text-gray-900 mb-12 leading-tight">
            Connecting <span className="italic text-orange-500">Insights</span>{" "}
            to Impact.
          </h2>
          <div className="flex flex-col md:flex-row items-center justify-center gap-6 mb-16">
            <a
              href={DATA.personal.bookingLink}
              target="_blank"
              rel="noreferrer"
              className="px-10 py-5 bg-gray-900 text-white rounded-full font-bold uppercase tracking-widest text-[10px] hover:bg-orange-600 transition-all shadow-xl flex items-center gap-3"
            >
              <Calendar size={16} /> Contact Krystal
            </a>
            <a
              href={DATA.personal.linkedin}
              target="_blank"
              rel="noreferrer"
              className="px-10 py-5 border border-gray-200 text-gray-900 rounded-full font-bold uppercase tracking-widest text-[10px] hover:border-black transition-all flex items-center gap-3"
            >
              <Linkedin size={16} /> LinkedIn
            </a>
          </div>
          <div className="pt-12 border-t border-gray-50 flex flex-col md:flex-row justify-between items-center text-[10px] font-bold tracking-[0.2em] text-gray-400 uppercase">
            <span>
              © {new Date().getFullYear()} {DATA.personal.name} • Based in NYC
            </span>
          </div>
        </div>
      </footer>
    </div>
  );
}
